import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Clock, Check, X, Calendar } from 'lucide-react';

const timeSlots = [
  '09:00', '11:00', '13:00', '15:00', '17:00'
];

const getDaysInMonth = (date) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startingDayOfWeek = firstDay.getDay();
  return { daysInMonth, startingDayOfWeek };
};

// Backgrounds available per studio (for display)
const studioBackgrounds = {
  'solo-studio': [
    { id: 'black', name: 'Professional Black' },
    { id: 'brown', name: 'Warm Brown' },
  ],
  'podcast-studio': [
    { id: 'corporate', name: 'Elegant Corporate' },
    { id: 'tropical', name: 'Tropical Bali' },
  ]
};

const studioNames = {
  'solo-studio': 'Solo Creator Room',
  'podcast-studio': 'Interview Room'
};

export const MultiStudioCalendar = ({ studioConfig, multiStudioSlots, onSelectSlot }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState('');
  const [activeStudio, setActiveStudio] = useState('studio1');

  const studio1 = studioConfig?.multiStudio?.studio1;
  const studio2 = studioConfig?.multiStudio?.studio2;
  
  const maxSlots1 = Math.ceil((studio1?.hours || 2) / 2);
  const maxSlots2 = Math.ceil((studio2?.hours || 2) / 2);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
  };

  const formatTime = (time) => {
    const hour = parseInt(time.split(':')[0]);
    return `${time} - ${hour + 2}:00`;
  };

  const handleSlotClick = (slotKey) => {
    onSelectSlot(activeStudio, slotKey);
  };

  const isSlotTakenByOther = (slotKey) => {
    // Check if slot is already taken by the other studio
    if (activeStudio === 'studio1') {
      return multiStudioSlots.studio2.includes(slotKey);
    }
    return multiStudioSlots.studio1.includes(slotKey);
  };

  const currentSlots = activeStudio === 'studio1' ? multiStudioSlots.studio1 : multiStudioSlots.studio2;
  const maxSlots = activeStudio === 'studio1' ? maxSlots1 : maxSlots2;
  const currentStudioConfig = activeStudio === 'studio1' ? studio1 : studio2;

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.5 }}
    >
      <h2 className="text-3xl md:text-4xl font-heading font-semibold mb-4 text-center">Select Time Slots</h2>
      <p className="text-center text-sm text-foreground-muted mb-8">
        Choose time slots for each studio separately
      </p>

      {/* Studio Tabs */}
      <div className="flex gap-4 mb-8">
        <button
          onClick={() => setActiveStudio('studio1')}
          className={`flex-1 p-4 border-2 transition-all duration-300 ${
            activeStudio === 'studio1'
              ? 'border-primary bg-primary/10'
              : 'border-border hover:border-primary/50'
          }`}
          data-testid="tab-studio1"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-primary">Studio 1</span>
            <span className={`text-xs px-2 py-1 rounded ${
              multiStudioSlots.studio1.length >= maxSlots1 
                ? 'bg-green-500/20 text-green-400' 
                : 'bg-primary/20 text-primary'
            }`}>
              {multiStudioSlots.studio1.length}/{maxSlots1} slots
            </span>
          </div>
          <p className="text-sm font-medium text-left">{studioNames[studio1?.studioId] || 'Studio 1'}</p>
          <p className="text-xs text-foreground-muted text-left">{studio1?.hours}h</p>
        </button>

        <button
          onClick={() => setActiveStudio('studio2')}
          className={`flex-1 p-4 border-2 transition-all duration-300 ${
            activeStudio === 'studio2'
              ? 'border-[#9333EA] bg-[#9333EA]/10'
              : 'border-border hover:border-[#9333EA]/50'
          }`}
          data-testid="tab-studio2"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-[#9333EA]">Studio 2</span>
            <span className={`text-xs px-2 py-1 rounded ${
              multiStudioSlots.studio2.length >= maxSlots2 
                ? 'bg-green-500/20 text-green-400' 
                : 'bg-[#9333EA]/20 text-[#9333EA]'
            }`}>
              {multiStudioSlots.studio2.length}/{maxSlots2} slots
            </span>
          </div>
          <p className="text-sm font-medium text-left">{studioNames[studio2?.studioId] || 'Studio 2'}</p>
          <p className="text-xs text-foreground-muted text-left">{studio2?.hours}h</p>
        </button>
      </div>

      {/* Selected Slots Summary for Active Studio */}
      {currentSlots.length > 0 && (
        <div className={`mb-8 p-6 border ${
          activeStudio === 'studio1' 
            ? 'bg-primary/5 border-primary/20' 
            : 'bg-[#9333EA]/5 border-[#9333EA]/20'
        }`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className={`text-lg font-heading font-semibold ${
              activeStudio === 'studio1' ? 'text-primary' : 'text-[#9333EA]'
            }`}>
              {activeStudio === 'studio1' ? 'Studio 1' : 'Studio 2'} - Selected Slots
            </h3>
            <span className={`text-sm font-bold ${
              activeStudio === 'studio1' ? 'text-primary' : 'text-[#9333EA]'
            }`}>
              {currentSlots.length}/{maxSlots}
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {currentSlots.map((slot, index) => {
              const [date, time] = slot.split('_');
              return (
                <div
                  key={slot}
                  className="flex items-center justify-between p-3 bg-background border border-border hover:border-primary transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Calendar className={`w-4 h-4 ${
                      activeStudio === 'studio1' ? 'text-primary' : 'text-[#9333EA]'
                    }`} />
                    <div>
                      <p className="text-sm font-semibold">{formatDate(date)}</p>
                      <p className="text-xs text-foreground-muted">{formatTime(time)}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleSlotClick(slot)}
                    className="p-1 hover:bg-primary/10 transition-colors"
                    data-testid={`remove-slot-${activeStudio}-${index}`}
                  >
                    <X className="w-4 h-4 text-foreground-muted hover:text-primary" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-8">
        {/* Calendar */}
        <div className="bg-muted/30 p-6 border border-border">
          {/* Month Navigation */}
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
              className="p-2 hover:bg-primary/10 transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <h3 className="text-lg font-heading font-semibold">
              {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </h3>
            <button
              onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
              className="p-2 hover:bg-primary/10 transition-colors"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* Day Headers */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="text-center text-xs text-foreground-muted py-2">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1">
            {(() => {
              const { daysInMonth, startingDayOfWeek } = getDaysInMonth(currentMonth);
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const days = [];
              
              // Empty cells for days before the first of the month
              for (let i = 0; i < startingDayOfWeek; i++) {
                days.push(<div key={`empty-${i}`} className="aspect-square" />);
              }
              
              // Days of the month
              for (let day = 1; day <= daysInMonth; day++) {
                const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
                const dateStr = date.toISOString().split('T')[0];
                const isPast = date < today;
                const isSelected = selectedDate === dateStr;
                
                days.push(
                  <button
                    key={day}
                    onClick={() => !isPast && setSelectedDate(dateStr)}
                    disabled={isPast}
                    className={`aspect-square flex items-center justify-center text-sm transition-all duration-200 ${
                      isPast
                        ? 'text-foreground-muted/30 cursor-not-allowed'
                        : isSelected
                          ? activeStudio === 'studio1'
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-[#9333EA] text-white'
                          : 'hover:bg-primary/10'
                    }`}
                    data-testid={`calendar-day-${day}`}
                  >
                    {day}
                  </button>
                );
              }
              
              return days;
            })()}
          </div>
        </div>

        {/* Time Slots */}
        <div>
          <h3 className="text-lg font-heading font-semibold mb-4 flex items-center gap-2">
            <Clock className={`w-5 h-5 ${
              activeStudio === 'studio1' ? 'text-primary' : 'text-[#9333EA]'
            }`} />
            Available Times
            {selectedDate && (
              <span className="text-sm font-normal text-foreground-muted">
                - {formatDate(selectedDate)}
              </span>
            )}
          </h3>
          
          {!selectedDate ? (
            <div className="bg-muted/30 p-8 border border-border text-center">
              <Calendar className="w-12 h-12 mx-auto mb-4 text-foreground-muted" />
              <p className="text-foreground-muted">Select a date to see available time slots</p>
            </div>
          ) : (
            <div className="space-y-3">
              {timeSlots.map((time) => {
                const slotKey = `${selectedDate}_${time}`;
                const isSelected = currentSlots.includes(slotKey);
                const isTakenByOther = isSlotTakenByOther(slotKey);
                const isDisabled = isTakenByOther || (!isSelected && currentSlots.length >= maxSlots);
                
                return (
                  <button
                    key={time}
                    onClick={() => !isDisabled && handleSlotClick(slotKey)}
                    disabled={isDisabled}
                    className={`w-full p-4 border-2 flex items-center justify-between transition-all duration-200 ${
                      isSelected
                        ? activeStudio === 'studio1'
                          ? 'border-primary bg-primary/10'
                          : 'border-[#9333EA] bg-[#9333EA]/10'
                        : isTakenByOther
                          ? 'border-border bg-muted/50 opacity-50 cursor-not-allowed'
                          : isDisabled
                            ? 'border-border opacity-50 cursor-not-allowed'
                            : 'border-border hover:border-primary/50'
                    }`}
                    data-testid={`time-slot-${time.replace(':', '')}`}
                  >
                    <div className="flex items-center gap-3">
                      <Clock className={`w-4 h-4 ${
                        isSelected 
                          ? activeStudio === 'studio1' ? 'text-primary' : 'text-[#9333EA]'
                          : 'text-foreground-muted'
                      }`} />
                      <span className="font-medium">{formatTime(time)}</span>
                    </div>
                    {isSelected && (
                      <Check className={`w-5 h-5 ${
                        activeStudio === 'studio1' ? 'text-primary' : 'text-[#9333EA]'
                      }`} />
                    )}
                    {isTakenByOther && (
                      <span className={`text-xs px-2 py-1 rounded ${
                        activeStudio === 'studio1' 
                          ? 'bg-[#9333EA]/20 text-[#9333EA]' 
                          : 'bg-primary/20 text-primary'
                      }`}>
                        {activeStudio === 'studio1' ? 'Studio 2' : 'Studio 1'}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* All Slots Summary */}
      <div className="mt-8 p-6 bg-muted/30 border border-border">
        <h3 className="text-lg font-heading font-semibold mb-4">Total Selection</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div className={`p-4 border ${
            multiStudioSlots.studio1.length >= maxSlots1 
              ? 'border-green-500/50 bg-green-500/5' 
              : 'border-primary/30 bg-primary/5'
          }`}>
            <div className="flex items-center justify-between mb-2">
              <span className="font-semibold text-primary">Studio 1</span>
              <span className={`text-sm ${
                multiStudioSlots.studio1.length >= maxSlots1 ? 'text-green-400' : 'text-primary'
              }`}>
                {multiStudioSlots.studio1.length >= maxSlots1 ? '✓ Complete' : `${multiStudioSlots.studio1.length}/${maxSlots1}`}
              </span>
            </div>
            <p className="text-sm text-foreground-muted">{studioNames[studio1?.studioId]} - {studio1?.hours}h</p>
          </div>
          <div className={`p-4 border ${
            multiStudioSlots.studio2.length >= maxSlots2 
              ? 'border-green-500/50 bg-green-500/5' 
              : 'border-[#9333EA]/30 bg-[#9333EA]/5'
          }`}>
            <div className="flex items-center justify-between mb-2">
              <span className="font-semibold text-[#9333EA]">Studio 2</span>
              <span className={`text-sm ${
                multiStudioSlots.studio2.length >= maxSlots2 ? 'text-green-400' : 'text-[#9333EA]'
              }`}>
                {multiStudioSlots.studio2.length >= maxSlots2 ? '✓ Complete' : `${multiStudioSlots.studio2.length}/${maxSlots2}`}
              </span>
            </div>
            <p className="text-sm text-foreground-muted">{studioNames[studio2?.studioId]} - {studio2?.hours}h</p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};
